import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  Download,
  Trash2,
  CheckCircle,
  Clock,
  RefreshCw,
  FileText,
  User,
  Award,
  X,
  Send,
  Eye,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  Building,
  Filter
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { capitalizeWords, formatDate } from '../../../utils/stringUtils';
import { formatIndianNumber } from '../../../utils/amountFormatter';
import { usePermissions } from '../../../hooks/usePermissions';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { get, post, patch, del } from '../../../api/apiClient';
import departmentRoleService from '../../../api/services/departmentRoleService';
import { designationService } from '../../../api/crm/config/configService';
import Button from '../../../components/common/Button/Button';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import toast from 'react-hot-toast';

const ExperienceCertificate = ({ onBack, userRole = 'hr_manager' }) => {
  const { hasPermission, canCreate, canUpdate, canDelete } = usePermissions();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialFilterMount = useRef(true);

  // State management — initialized from URL
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCertificateForm, setShowCertificateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [departmentFilter, setDepartmentFilter] = useState(searchParams.get('department') || 'all');
  const [formErrors, setFormErrors] = useState({});
  const [certificatesPage, setCertificatesPage] = useState(Number(searchParams.get('page')) || 1);
  const [certificatesPageSize, setCertificatesPageSize] = useState(Number(searchParams.get('limit')) || 20);
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || '');
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || '');
  const [sortConfig, setSortConfig] = useState({
    key: searchParams.get('sortBy') || 'createdAt',
    direction: searchParams.get('sortOrder') || 'desc',
  });
  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [alertInfo, setAlertInfo] = useState(null);

  // Reset pagination when filters change (skip initial mount to preserve URL page on refresh)
  useEffect(() => {
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
      return;
    }
    setCertificatesPage(1);
  }, [debouncedSearchTerm, statusFilter, departmentFilter, startDate, endDate]);

  // Sync filter/sort/pagination state to URL search params (persists on refresh)
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      const managedKeys = [
        'search',
        'status',
        'department',
        'startDate',
        'endDate',
        'page',
        'limit',
        'sortBy',
        'sortOrder',
      ];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (departmentFilter && departmentFilter !== 'all') params.set('department', departmentFilter);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (certificatesPage && certificatesPage !== 1) params.set('page', String(certificatesPage));
      if (certificatesPageSize && certificatesPageSize !== 20) params.set('limit', String(certificatesPageSize));
      if (sortConfig.key && sortConfig.key !== 'createdAt') params.set('sortBy', sortConfig.key);
      if (sortConfig.direction && sortConfig.direction !== 'desc') params.set('sortOrder', sortConfig.direction);

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    statusFilter,
    departmentFilter,
    startDate,
    endDate,
    certificatesPage,
    certificatesPageSize,
    sortConfig,
    setSearchParams,
  ]);

  // Export State
  const [selectedRows, setSelectedRows] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Additional UI State
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [certificateToSend, setCertificateToSend] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [certificateToView, setCertificateToView] = useState(null);

  const { data: designationMasterResponse = [], isLoading: loadingDesignationMasters } = useQuery({
    queryKey: ['designations', 'active'],
    queryFn: async () => {
      const result = await designationService.getAll({ isActive: true });
      return result?.data || [];
    }
  });

  const designationMasters = useMemo(() => {
    const list = Array.isArray(designationMasterResponse) ? designationMasterResponse : [];
    return [...list].sort((a, b) => {
      const orderA = Number(a.order || 0);
      const orderB = Number(b.order || 0);
      if (orderA !== orderB) return orderA - orderB;
      return String(a.title || a.name || '').localeCompare(String(b.title || b.name || ''));
    });
  }, [designationMasterResponse]);

  // Preview Modal State
  const [showPreview, setShowPreview] = useState(false);
  const [previewCertificate, setPreviewCertificate] = useState(null);

  // Signatory State
  const [signatorySearchTerm, setSignatorySearchTerm] = useState('');
  const [isSignatoryDropdownOpen, setIsSignatoryDropdownOpen] = useState(false);
  const [selectedSignatory, setSelectedSignatory] = useState(null);
  const [hasAutoSelectedSignatory, setHasAutoSelectedSignatory] = useState(false);
  const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);

  // Refs
  const employeeDropdownRef = useRef(null);
  const signatoryDropdownRef = useRef(null);
  const departmentDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  // Form state
  const [certificateForm, setCertificateForm] = useState({
    employeeId: '',
    exitRequestId: '',
    employmentDetails: {
      joinDate: '',
      lastWorkingDate: '',
      employmentType: 'full-time',
      workLocation: '',
      reportingManager: '',
      reasonForLeaving: 'resignation',
      reasonDescription: ''
    },
    performanceDetails: {
      overallRating: 'good',
      keyAchievements: [{ achievement: '', year: new Date().getFullYear() }],
      skills: [],
      performanceNotes: '',
      performanceRemark: ''
    },
    financialDetails: {
      lastSalary: '',
      currency: 'INR',
      salaryMentioned: false,
      benefits: [{ benefitType: '', amount: '' }],
      finalSettlement: '',
      pendingDues: ''
    },
    authorization: {
      signatoryId: '',
      digitalSignatureUrl: '',
      companySealUrl: ''
    },
    certificateContent: {
      subject: '',
      salutation: 'To Whom It May Concern,',
      body: '',
      closing: 'Yours sincerely,',
      signature: '',
      designation: '',
      companyName: '',
      companyAddress: ''
    },
    documentControl: {
      letterNumber: '',
      issueDate: '',
      isLocked: false,
      status: 'draft'
    },
    additionalRemark: '',
    remarks: ''
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounce employee search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [employeeSearchTerm]);

  // React Query: Load certificates
  const { data: certificatesData, isLoading: isLoadingCertificates } = useQuery({
    queryKey: ['experienceCertificates', certificatesPage, certificatesPageSize, statusFilter, departmentFilter, debouncedSearchTerm, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(certificatesPage));
      params.set('limit', String(certificatesPageSize));
      if (statusFilter && statusFilter !== 'all') {
        // Send status directly - backend will handle 'issued' -> 'sent' mapping if needed
        params.set('status', statusFilter);
      }
      if (departmentFilter && departmentFilter !== 'all') params.set('department', departmentFilter);
      if (debouncedSearchTerm && debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const result = await get('/experience-certificate', { params: Object.fromEntries(params) });
      const data = result.data || {};
      return {
        certificates: data.certificates || data || [],
        pagination: data.pagination || { totalPages: 1, totalItems: 0 }
      };
    },
    enabled: true,
    refetchInterval: 0
  });

  const certificates = certificatesData?.certificates || [];
  const certificatesTotalPages = certificatesData?.pagination?.totalPages || 1;
  const certificatesTotalItems = certificatesData?.pagination?.totalItems || 0;

  // React Query: Load statistics from backend
  const { data: statisticsData } = useQuery({
    queryKey: ['experienceCertificateStatistics'],
    queryFn: async () => {
      const result = await get('/experience-certificate/stats');
      return result.data || { totalCertificates: 0, draftCertificates: 0, sentCertificates: 0 };
    },
    enabled: true,
    refetchInterval: 0,
  });

  const certificateStats = statisticsData || { totalCertificates: 0, draftCertificates: 0, sentCertificates: 0 };

  useEffect(() => {
    if (selectedRows.length === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.length]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const baseFilters = useMemo(() => {
    const filters = { startDate, endDate };
    if (debouncedSearchTerm) filters.search = debouncedSearchTerm;
    if (statusFilter && statusFilter !== 'all') filters.status = statusFilter;
    if (departmentFilter && departmentFilter !== 'all') filters.department = departmentFilter;
    return filters;
  }, [statusFilter, departmentFilter, debouncedSearchTerm, startDate, endDate]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const blob = await post('/exports/generate', {
        module: 'experience-certificates',
        format,
        scope,
        filters: overrideFilters ?? baseFilters,
        page: overridePage ?? certificatesPage,
        limit: overrideLimit ?? certificatesPageSize,
        sortBy: sortConfig.key || 'createdAt',
        sortOrder: sortConfig.direction,
        selectedIds,
      }, { responseType: 'blob' });
      const filename = `experience-certificates-export.${format === 'xlsx' ? 'xlsx' : format}`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseFilters, certificatesPage, certificatesPageSize, sortConfig]
  );

  const handleExportClick = () => {
    if (selectedRows.length > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      if (!selectedRows.length) {
        toast.error('No selected certificates found to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: selectedRows,
        });
        toast.success(`${selectedRows.length} certificate(s) exported!`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      setExportLoading(true);
      try {
        const options = { scope, format };
        if (scope === 'all') {
          options.overrideFilters = {};
        } else if (scope === 'dateRange' && meta.startDate && meta.endDate) {
          const filters = {
            startDate: meta.startDate,
            endDate: meta.endDate,
          };
          if (debouncedSearchTerm) filters.search = debouncedSearchTerm;
          if (statusFilter && statusFilter !== 'all') filters.status = statusFilter;
          if (departmentFilter && departmentFilter !== 'all') filters.department = departmentFilter;
          options.overrideFilters = filters;
        }
        await requestBackendExport(options);
        toast.success('Export started successfully!');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [requestBackendExport, debouncedSearchTerm, statusFilter, departmentFilter]
  );
  const { data: employeesData, isLoading: isLoadingEmployees } = useQuery({
    queryKey: ['experienceCertificateEmployees', debouncedEmployeeSearch],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('status', 'approved');
      params.set('limit', '1000'); // Get all approved exit requests
      if (debouncedEmployeeSearch && debouncedEmployeeSearch.trim()) {
        params.set('search', debouncedEmployeeSearch.trim());
      }

      const result = await get('/exit', { params: Object.fromEntries(params) });
      const exitRequests = result.data?.exitRequests || [];

      // Extract unique employees from exit requests and store exit request data with each employee
      const employeeMap = new Map();
      exitRequests.forEach((exitRequest) => {
        const employee = exitRequest.employeeId;
        if (employee && employee._id) {
          const employeeId = employee._id.toString();
          if (!employeeMap.has(employeeId)) {
            // Format employee data similar to the previous structure
            const firstName = employee.personalInfo?.firstName || '';
            const lastName = employee.personalInfo?.lastName || '';
            // Use fullName from API if available, otherwise construct from firstName/lastName
            // Construct clean full name from personal info
            const constructedName = `${firstName} ${lastName}`.trim();
            // Use fullName from API only if it doesn't contain the string "undefined", otherwise use constructed name
            const fullName = (employee.fullName && !employee.fullName.includes('undefined'))
              ? employee.fullName
              : (constructedName || exitRequest.employeeName || '');
            const employeeCode = employee.employeeId || employeeId;
            const department = employee.employmentInfo?.department || exitRequest.department || '';
            const email = employee.contactInfo?.personalEmail || employee.contactInfo?.workEmail || exitRequest.employeeEmail || '';
            const officialEmail = employee.user?.email || '';

            employeeMap.set(employeeId, {
              _id: employee._id,
              employeeId: employeeCode,
              id: employee._id,
              fullName: fullName,
              firstName: firstName,
              lastName: lastName,
              employeeName: fullName,
              personalInfo: employee.personalInfo || {},
              employmentInfo: employee.employmentInfo || {},
              contactInfo: employee.contactInfo || {},
              department: department,
              email: email,
              officialEmail: officialEmail,
              // Store the exit request data for auto-filling
              exitRequestData: exitRequest
            });
          }
        }
      });

      return Array.from(employeeMap.values());
    },
    enabled: showCertificateForm,
  });

  // Toggle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = certificates.map(c => c._id);
      setSelectedRows(allIds);
    } else {
      setSelectedRows([]);
    }
  };

  // Toggle select row
  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleExport = () => {
    handleExportClick();
  };

  // Clear toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setAlertInfo(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const employees = employeesData || [];


  const selectedEmployeeRecord = useMemo(() => {
    return (employees || []).find((emp) => (emp._id || emp.id || emp.employeeId) === certificateForm.employeeId);
  }, [employees, certificateForm.employeeId]);

  const selectedEmployeeName = useMemo(() => {
    if (!selectedEmployeeRecord) return '';
    const { fullName, firstName, lastName, employeeName } = selectedEmployeeRecord;
    const constructed = `${firstName || ''} ${lastName || ''}`.trim();
    if (fullName && !fullName.includes('undefined')) return fullName;
    if (constructed) return constructed;
    if (employeeName && !employeeName.includes('undefined')) return employeeName;
    return '—';
  }, [selectedEmployeeRecord]);

  const tenureLabel = useMemo(() => {
    const join = certificateForm.employmentDetails.joinDate;
    const lwd = certificateForm.employmentDetails.lastWorkingDate;
    if (!join || !lwd) return '—';

    const start = new Date(join);
    const end = new Date(lwd);
    if (isNaN(start) || isNaN(end) || start > end) return '—';

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0).getDate();
      days += prevMonth;
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    const parts = [];
    if (years > 0) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
    if (months > 0) parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
    if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);

    return parts.length > 0 ? parts.join(', ') : '0 days';
  }, [certificateForm.employmentDetails.joinDate, certificateForm.employmentDetails.lastWorkingDate]);
  // React Query: Load signatories (Super Admin / HR Manager) - Fetched from backend
  const { data: signatoriesData, isLoading: isLoadingSignatories } = useQuery({
    queryKey: ['certificateSignatories'],
    queryFn: async () => {
      const result = await get('/experience-certificate/signatories');
      return result.data || [];
    },
    enabled: true,
    staleTime: 0,
  });

  const signatories = signatoriesData || [];

  // Helper to find Javed Akhtar or first available
  const getDefaultSignatory = (sigs) => {
    if (!sigs || sigs.length === 0) return null;
    const getSigName = (sig) => {
      return sig.fullName ||
        (sig.personalInfo ? `${sig.personalInfo.firstName || ''} ${sig.personalInfo.lastName || ''}`.trim() : '') ||
        sig.name || 'Unknown';
    };
    return sigs.find(s => getSigName(s).toLowerCase().includes('javed akhtar')) || sigs[0];
  };

  // React Query: Load exit requests
  const { data: exitRequestsData, refetch: refetchExitRequests } = useQuery({
    queryKey: ['experienceCertificateExitRequests'],
    queryFn: async () => {
      const result = await get('/exit');
      return result.data?.exitRequests || [];
    },
    enabled: showCertificateForm,
  });

  const exitRequests = exitRequestsData || [];

  // React Query: Load departments
  const { data: departmentsData } = useQuery({
    queryKey: ['experienceCertificateDepartments'],
    queryFn: async () => {
      try {
        const resp = await departmentRoleService.getDepartments();
        const raw = resp?.data || resp?.departments || resp || [];
        const names = (Array.isArray(raw) ? raw : [])
          .map((d) => (typeof d === 'string' ? d : d?.name || d?.departmentName || d?.label || d?.value))
          .filter(Boolean);
        return Array.from(new Set(names));
      } catch (err) {
        return [];
      }
    },
    enabled: true,
  });

  const departments = departmentsData || [];

  // Close Employee & Signatory dropdowns on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(e.target)) {
        setIsEmployeeDropdownOpen(false);
      }
      if (isSignatoryDropdownOpen && signatoryDropdownRef.current && !signatoryDropdownRef.current.contains(e.target)) {
        setIsSignatoryDropdownOpen(false);
      }
      if (isDepartmentDropdownOpen && departmentDropdownRef.current && !departmentDropdownRef.current.contains(e.target)) {
        setIsDepartmentDropdownOpen(false);
      }
      if (isStatusDropdownOpen && statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setIsStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isSignatoryDropdownOpen, isDepartmentDropdownOpen, isStatusDropdownOpen]);

  // Reset auto-select flag when form closes
  useEffect(() => {
    if (!showCertificateForm) {
      setHasAutoSelectedSignatory(false);
    }
  }, [showCertificateForm]);


  // Helper function for text input key press (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    const char = e.key;
    if (!/^[a-zA-Z0-9\s.,/&-]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'ArrowLeft' && char !== 'ArrowRight' && char !== 'Tab' && char !== 'Enter') {
      e.preventDefault();
    }
  };

  // Helper function for number input key down (positive numbers only, 0 and greater)
  const handleNumberKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrow keys, and numbers
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow numbers on top of keyboard
      (e.keyCode >= 48 && e.keyCode <= 57) ||
      // Allow numbers on numpad
      (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    // Block: minus, plus, e, E, period (for decimal)
    if (e.keyCode === 189 || e.keyCode === 187 || e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 110) {
      e.preventDefault();
    }
  };

  // Helper function for year input key down (4 digits only)
  const handleYearKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrow keys, and numbers
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow numbers on top of keyboard
      (e.keyCode >= 48 && e.keyCode <= 57) ||
      // Allow numbers on numpad
      (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    // Block: minus, plus, e, E, period
    if (e.keyCode === 189 || e.keyCode === 187 || e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 110) {
      e.preventDefault();
    }
  };

  // Helper function for search input key press (alphanumeric, spaces, and email special characters)
  const handleSearchKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Inline validation helpers
  const setFieldError = (key, message) => {
    setFormErrors((prev) => ({ ...prev, [key]: message || undefined }));
  };

  const validateField = (key, value) => {
    const val = typeof value === 'string' ? value.trim() : value;
    switch (key) {
      case 'employeeId':
        setFieldError(key, val ? '' : 'Please select an employee');
        break;
      case 'employment.joinDate':
        setFieldError(key, val ? '' : 'Join date is required');
        break;
      case 'employment.lastWorkingDate': {
        const jd = new Date(certificateForm.employmentDetails.joinDate);
        const lwd = new Date(val);
        setFieldError(key, val && (!certificateForm.employmentDetails.joinDate || lwd > jd) ? '' : 'Last working date must be after join date');
        break;
      }
      case 'employment.employmentType':
        setFieldError(key, val ? '' : 'Employment type is required');
        break;
      case 'employment.workLocation':
        setFieldError(key, val ? '' : 'Work location is required');
        break;
      case 'financial.lastSalary': {
        if (certificateForm.financialDetails.salaryMentioned) {
          const n = Number(val);
          setFieldError(key, Number.isFinite(n) && n > 0 ? '' : 'Last salary must be greater than 0 if mentioned');
        } else {
          setFieldError(key, '');
        }
        break;
      }
      case 'performance.performanceRemark':
        setFieldError(key, val ? '' : 'Performance remark is mandatory for enterprise readiness');
        break;
      case 'authorization.signatoryId':
        setFieldError(key, val ? '' : 'Authorized signatory is required');
        break;
      case 'content.subject':
        setFieldError(key, val && val.length >= 3 ? '' : 'Subject must be at least 3 characters');
        break;
      case 'content.body':
        setFieldError(key, val && val.length >= 10 ? '' : 'Body must be at least 10 characters');
        break;
      case 'remarks':
        if (!val) {
          setFieldError(key, 'Remarks are required');
        } else if (val.length > 500) {
          setFieldError(key, 'Remarks cannot exceed 500 characters');
        } else {
          setFieldError(key, '');
        }
        break;
      default:
        break;
    }
  };

  const validateAll = () => {
    validateField('employeeId', certificateForm.employeeId);
    validateField('performance.performanceRemark', certificateForm.performanceDetails.performanceRemark);
    validateField('authorization.signatoryId', certificateForm.authorization.signatoryId);
    validateField('remarks', certificateForm.remarks);
    if (certificateForm.financialDetails.salaryMentioned) {
      validateField('financial.lastSalary', certificateForm.financialDetails.lastSalary);
    }
    return !Object.values({ ...formErrors }).some(Boolean);
  };

  // Auto-fill Employment Details when Employee is selected
  useEffect(() => {
    if (!certificateForm.employeeId) {
      // Clear signatory details if employee is deselected
      setCertificateForm(prev => ({
        ...prev,
        authorization: { ...prev.authorization, signatoryId: '' },
        certificateContent: { ...prev.certificateContent, signature: '', designation: '' }
      }));
      setSelectedSignatory(null);
      return;
    }

    const selectedEmployee = (employees || []).find((emp) => (emp._id || emp.id || emp.employeeId) === certificateForm.employeeId);
    if (!selectedEmployee) return;

    const emp = selectedEmployee;
    const empInfo = emp.employmentInfo || {};
    const exitReq = emp.exitRequestData || {};

    const joinDate = empInfo.joinDate ? String(empInfo.joinDate).slice(0, 10) : (emp.joinDate ? String(emp.joinDate).slice(0, 10) : certificateForm.employmentDetails.joinDate);
    const lwd = exitReq.lastWorkingDate;
    const lastWorkingDate = lwd ? new Date(lwd).toISOString().slice(0, 10) : certificateForm.employmentDetails.lastWorkingDate;
    const employmentType = empInfo.employmentType || certificateForm.employmentDetails.employmentType;
    const rawWorkLocation = empInfo.workLocation || certificateForm.employmentDetails.workLocation;
    const workLocation = typeof rawWorkLocation === 'object' && rawWorkLocation !== null
      ? (rawWorkLocation.name || rawWorkLocation.label || '')
      : rawWorkLocation;
    const reasonForLeaving = exitReq.resignationType || certificateForm.employmentDetails.reasonForLeaving;
    const reasonDescription = exitReq.detailedReason || exitReq.reason || certificateForm.employmentDetails.reasonDescription;
    const reportingManager = empInfo.reportingManager?.fullName ||
      empInfo.reportingManager?.name ||
      (typeof empInfo.reportingManager === 'string' ? empInfo.reportingManager : '');

    // Auto-select reporting manager as signatory if they are authorized, otherwise use global default
    const managerId = empInfo.reportingManager?._id || empInfo.reportingManager?.id;
    let signatoryUpdates = {};
    if (signatories.length > 0) {
      const getSigName = (sig) => {
        return sig.fullName ||
          (sig.personalInfo ? `${sig.personalInfo.firstName || ''} ${sig.personalInfo.lastName || ''}`.trim() : '') ||
          sig.name || 'Unknown';
      };

      const matchedSignatory = managerId ? signatories.find(s => String(s._id || s.id) === String(managerId)) : null;
      const targetSig = matchedSignatory || getDefaultSignatory(signatories);

      if (targetSig) {
        const sigId = targetSig._id || targetSig.id;
        const sigName = getSigName(targetSig);
        signatoryUpdates = {
          authorization: { ...certificateForm.authorization, signatoryId: String(sigId) },
          certificateContent: {
            ...certificateForm.certificateContent,
            signature: sigName,
            designation: targetSig.employmentInfo?.position || targetSig.employmentInfo?.designation || targetSig.designation || 'Signatory'
          }
        };
        setSelectedSignatory(targetSig);
        setFormErrors(prev => ({ ...prev, 'authorization.signatoryId': undefined }));
      }
    }

    setCertificateForm((prev) => ({
      ...prev,
      ...signatoryUpdates,
      exitRequestId: exitReq._id || prev.exitRequestId,
      employmentDetails: {
        ...prev.employmentDetails,
        joinDate,
        lastWorkingDate,
        employmentType,
        workLocation,
        reportingManager,
        reasonForLeaving,
        reasonDescription
      }
    }));
  }, [certificateForm.employeeId, employees.length, signatories.length]);


  // React Query: Create certificate mutation
  const createCertificateMutation = useMutation({
    mutationFn: async (payload) => {
      return post('/experience-certificate', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['experienceCertificates']);
      queryClient.invalidateQueries(['experienceCertificateStatistics']);
      setAlertInfo({
        type: 'success',
        title: 'Success',
        message: 'Experience certificate snapshot generated successfully!'
      });
      setShowCertificateForm(false);
      resetCertificateForm();
    },
    onError: (error) => {
      const errorMessage = error.response?.status === 409
        ? 'Experience certificate already exists for this employee.'
        : 'Failed to create experience certificate: ' + error.message;
      setAlertInfo({
        type: 'error',
        title: 'Error',
        message: errorMessage
      });
    },
  });

  // React Query: Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ certificateId, newStatus }) => {
      return patch(`/experience-certificate/${certificateId}/status`, { status: newStatus });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['experienceCertificates']);
      queryClient.invalidateQueries(['experienceCertificateStatistics']);
    },
    onError: (error) => {
      setAlertInfo({
        type: 'error',
        title: 'Error',
        message: 'Failed to update experience certificate status: ' + error.message
      });
    },
  });

  // React Query: Approve certificate mutation
  const approveCertificateMutation = useMutation({
    mutationFn: async (certificateId) => {
      return patch(`/experience-certificate/${certificateId}/approve`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['experienceCertificates']);
      queryClient.invalidateQueries(['experienceCertificateStatistics']);
    },
    onError: (error) => {
      setAlertInfo({
        type: 'error',
        title: 'Error',
        message: 'Failed to approve experience certificate: ' + error.message
      });
    },
  });

  // React Query: Issue certificate mutation
  const issueCertificateMutation = useMutation({
    mutationFn: async (certificateId) => {
      return patch(`/experience-certificate/${certificateId}/issue`, { deliveryMethod: 'email' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['experienceCertificates']);
      queryClient.invalidateQueries(['experienceCertificateStatistics']);
    },
    onError: (error) => {
      setAlertInfo({
        type: 'error',
        title: 'Error',
        message: 'Failed to issue experience certificate: ' + error.message
      });
    },
  });

  // React Query: Delete certificate mutation
  const deleteCertificateMutation = useMutation({
    mutationFn: async (certificateId) => {
      return del(`/experience-certificate/${certificateId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['experienceCertificates']);
      queryClient.invalidateQueries(['experienceCertificateStatistics']);
      setAlertInfo({
        type: 'success',
        title: 'Success',
        message: 'Experience certificate deleted successfully!'
      });
    },
    onError: (error) => {
      setAlertInfo({
        type: 'error',
        title: 'Error',
        message: 'Failed to delete experience certificate: ' + error.message
      });
    },
  });

  // Create experience certificate handler
  const handleGenerateCertificate = (e) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    const selectedEmployeeRecord = (employees || []).find(
      (emp) => (emp._id || emp.id || emp.employeeId) === certificateForm.employeeId
    );

    if (!selectedEmployeeRecord) {
      validateField('employeeId', certificateForm.employeeId);
      setAlertInfo({
        type: 'error',
        title: 'Selection Required',
        message: 'Please select an employee before generating the certificate.'
      });
      return;
    }

    const employeeObjectId = selectedEmployeeRecord._id || selectedEmployeeRecord.id || null;
    const employeeCode =
      selectedEmployeeRecord.employeeId ||
      selectedEmployeeRecord.employeeCode ||
      selectedEmployeeRecord.employeeNumber ||
      '';
    const employeeNameValue =
      selectedEmployeeRecord.fullName ||
      selectedEmployeeRecord.employeeName ||
      `${selectedEmployeeRecord.firstName || ''} ${selectedEmployeeRecord.lastName || ''}`.trim();
    const employeeEmailValue =
      selectedEmployeeRecord.email ||
      selectedEmployeeRecord.contactInfo?.personalEmail ||
      selectedEmployeeRecord.contactInfo?.workEmail ||
      '';
    const employeeDepartmentValue =
      selectedEmployeeRecord.department ||
      selectedEmployeeRecord.employmentInfo?.department ||
      '';
    const employeePositionValue =
      selectedEmployeeRecord.employmentInfo?.position ||
      selectedEmployeeRecord.employmentInfo?.designation ||
      '';
    const employmentInfo = selectedEmployeeRecord.employmentInfo || {};
    const exitData = selectedEmployeeRecord.exitRequestData || {};

    const joinDateValue =
      certificateForm.employmentDetails.joinDate ||
      employmentInfo.joinDate ||
      selectedEmployeeRecord.joinDate ||
      null;
    const lastWorkingDateValue =
      certificateForm.employmentDetails.lastWorkingDate ||
      exitData.lastWorkingDate ||
      exitData.proposedLastWorkingDate ||
      null;

    if (!employeeObjectId) {
      setAlertInfo({
        type: 'error',
        title: 'Identifier Error',
        message: 'Unable to resolve a valid employee identifier for this record.'
      });
      return;
    }

    if (!employeeCode) {
      setAlertInfo({
        type: 'error',
        title: 'Missing Data',
        message: 'Selected employee is missing an employee ID number.'
      });
      return;
    }

    const sanitiseDate = (value) => {
      if (!value) return null;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
    };

    const payload = {
      employeeId: employeeObjectId,
      exitRequestId: exitData?._id || null,
      noDuesClearanceId: exitData?.noDuesClearanceId || exitData?.noDuesId || null,
      employeeName: employeeNameValue,
      employeeEmail: employeeEmailValue,
      department: employeeDepartmentValue || 'Not assigned',
      position: employeePositionValue || 'Not specified',
      employeeIdNumber: employeeCode,
      employmentDetails: {
        joinDate: sanitiseDate(joinDateValue),
        lastWorkingDate: sanitiseDate(lastWorkingDateValue),
        employmentType:
          employmentInfo.employmentType ||
          certificateForm.employmentDetails.employmentType ||
          'full-time',
        workLocation: (() => {
          const val = employmentInfo.workLocation || certificateForm.employmentDetails.workLocation;
          return typeof val === 'object' && val !== null ? (val.name || val.label || '') : (val || '');
        })(),
        reportingManager:
          employmentInfo.reportingManager ||
          certificateForm.employmentDetails.reportingManager ||
          '',
        reasonForLeaving:
          exitData?.resignationType ||
          certificateForm.employmentDetails.reasonForLeaving ||
          'resignation',
        reasonDescription:
          exitData?.detailedReason ||
          exitData?.reason ||
          certificateForm.employmentDetails.reasonDescription ||
          ''
      },
      performanceDetails: certificateForm.performanceDetails,
      financialDetails: certificateForm.financialDetails,
      certificateContent: certificateForm.certificateContent,
      authorization: certificateForm.authorization,
      documentControl: certificateForm.documentControl,
      additionalRemark: certificateForm.additionalRemark,
      status: certificateForm.documentControl?.status || 'draft',
      remarks: certificateForm.remarks
    };

    setIsSubmitting(true);
    createCertificateMutation.mutate(payload, {
      onSettled: () => setIsSubmitting(false)
    });
  };

  // Update certificate status handler
  const handleStatusUpdate = (certificateId, newStatus) => {
    updateStatusMutation.mutate({ certificateId, newStatus });
  };

  // Approve certificate handler
  const handleApproveCertificate = (certificateId) => {
    approveCertificateMutation.mutate(certificateId);
  };

  // Issue certificate handler
  const handleIssueCertificate = (certificateId) => {
    issueCertificateMutation.mutate(certificateId);
  };

  // Show send confirmation modal
  const handleSendCertificateClick = (certificate) => {
    setCertificateToSend(certificate);
    setShowSendModal(true);
  };

  // Close send modal
  const handleCloseSendModal = () => {
    setShowSendModal(false);
    setCertificateToSend(null);
    setIsSending(false);
  };





  // Actually send certificate via email (called from modal)
  const handleSendCertificate = async () => {
    if (!certificateToSend) {
      return;
    }

    const certificate = certificateToSend;
    setIsSending(true);

    // Try to find the employee email
    const employeeEmail =
      certificate.employeeEmail ||
      certificate.officialEmail ||
      (certificate.employeeId && (certificate.employeeId.officialEmail || certificate.employeeId.personalInfo?.email)) ||
      '';

    if (!employeeEmail) {
      setAlertInfo({
        title: 'Error',
        message: 'Employee email not found. Cannot send certificate.',
        type: 'error',
      });
      setIsSending(false);
      return;
    }

    try {
      await post(`/experience-certificate/${certificate._id}/send`, {
        emailContent: null,
        employeeEmail,
      });
      setAlertInfo({ title: 'Success', message: 'Certificate sent successfully via email', type: 'success' });
      queryClient.invalidateQueries(['experienceCertificates']);
      queryClient.invalidateQueries(['experienceCertificateStatistics']);
      handleCloseSendModal();
      setIsSending(false);
    } catch (err) {
      setAlertInfo({ title: 'Error', message: err.message, type: 'error' });
      setIsSending(false);
    }
  };

  // Delete certificate handler
  const handleDeleteCertificate = (certificateId) => {
    setCertificateToDelete(certificateId);
    setShowDeleteConfirm(true);
  };

  // Confirm delete action
  const handleConfirmDelete = () => {
    if (certificateToDelete) {
      deleteCertificateMutation.mutate(certificateToDelete, {
        onSettled: () => {
          setShowDeleteConfirm(false);
          setCertificateToDelete(null);
        }
      });
    }
  };

  // Cancel delete action
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setCertificateToDelete(null);
  };

  // Show view modal
  const handleViewCertificate = (certificate) => {
    setCertificateToView(certificate);
    setShowViewModal(true);
  };

  // Close view modal
  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setCertificateToView(null);
  };


  // Generate default certificate body from form data (mirrors RelievingLetter.generateDefaultLetterBody)
  const generateDefaultCertificateBody = (formData, employeesList) => {
    const employee = (employeesList || []).find(e => {
      const val = e._id || e.id || e.employeeId;
      return String(val) === String(formData.employeeId);
    });

    const name = employee?.fullName || 'Employee';
    const id = employee?.employeeId || employee?._id || 'N/A';
    const designation = employee?.employmentInfo?.designation ||
      employee?.employmentInfo?.position ||
      formData.position || '';
    const dept = employee?.employmentInfo?.department ||
      employee?.department || formData.department || '';

    const formatDateNice = (dateStr) => {
      if (!dateStr) return 'N/A';
      try {
        return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      } catch (e) { return 'N/A'; }
    };

    const joinDate = formatDateNice(formData.employmentDetails?.joinDate);
    const lwd = formatDateNice(formData.employmentDetails?.lastWorkingDate);
    const companyName = process.env.REACT_APP_COMPANY_NAME;
    const positionText = designation && String(designation).toLowerCase() !== 'employee'
      ? `as a ${designation}` : 'as an Employee';
    const deptText = dept ? `in the ${dept} Department` : '';
    const performanceRemark = formData.performanceDetails?.performanceRemark ||
      formData.performanceDetails?.overallRating || 'Satisfactory';

    // Compute tenure
    let tenureText = '';
    if (formData.employmentDetails?.joinDate && formData.employmentDetails?.lastWorkingDate) {
      const start = new Date(formData.employmentDetails.joinDate);
      const end = new Date(formData.employmentDetails.lastWorkingDate);
      if (!isNaN(start) && !isNaN(end) && end >= start) {
        let y = end.getFullYear() - start.getFullYear();
        let m = end.getMonth() - start.getMonth();
        let d = end.getDate() - start.getDate();
        if (d < 0) { m--; d += new Date(end.getFullYear(), end.getMonth(), 0).getDate(); }
        if (m < 0) { y--; m += 12; }
        const parts = [];
        if (y > 0) parts.push(`${y} ${y === 1 ? 'year' : 'years'}`);
        if (m > 0) parts.push(`${m} ${m === 1 ? 'month' : 'months'}`);
        if (d > 0) parts.push(`${d} ${d === 1 ? 'day' : 'days'}`);
        tenureText = parts.length > 0 ? parts.join(', ') : '0 days';
      }
    }

    let body = `This is to certify that ${name} (Employee ID: ${id}) was employed with ${companyName} ${positionText} ${deptText}.\n\n`;
    body += `${name} joined the organization on ${joinDate}, and their last working day was ${lwd}.`;
    if (tenureText) body += ` During this period of ${tenureText}, they served the organization with dedication and professionalism.`;
    body += `\n\n`;
    body += `The employee's performance was found to be ${performanceRemark}. We found them to be sincere, hardworking, and professional in all assigned responsibilities.\n\n`;
    body += `We wish ${name} all the best in their future endeavors.`;

    return body;
  };

  // Handle preview from form (validates then opens preview modal)
  const handlePreviewFromForm = () => {
    if (!certificateForm.employeeId) {
      setAlertInfo({ type: 'error', title: 'Missing Employee', message: 'Please select an employee first.' });
      return;
    }

    const employee = (employees || []).find(
      e => (String(e._id) === String(certificateForm.employeeId) ||
        String(e.id) === String(certificateForm.employeeId) ||
        String(e.employeeId) === String(certificateForm.employeeId))
    );

    // Generate body if empty
    let currentBody = certificateForm.certificateContent?.body;
    if (!currentBody || currentBody.trim() === '') {
      currentBody = generateDefaultCertificateBody(certificateForm, employees);
    }

    // Ensure issueDate
    const currentIssueDate =
      certificateForm.documentControl?.issueDate ||
      new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

    // Update form state
    setCertificateForm(prev => ({
      ...prev,
      documentControl: { ...prev.documentControl, issueDate: currentIssueDate },
      certificateContent: {
        ...prev.certificateContent,
        body: currentBody,
        salutation: prev.certificateContent?.salutation || 'To Whom It May Concern,',
        companyName: process.env.REACT_APP_COMPANY_NAME,
      }
    }));

    const previewData = {
      ...certificateForm,
      documentControl: { ...certificateForm.documentControl, issueDate: currentIssueDate },
      employeeName: employee?.fullName || 'Employee',
      employeeIdNumber: employee?.employeeId || employee?._id || 'N/A',
      employeeEmail: employee?.email || employee?.contactInfo?.personalEmail || '',
      department: employee?.department || certificateForm.department || '',
      certificateContent: {
        ...certificateForm.certificateContent,
        body: currentBody,
        salutation: certificateForm.certificateContent?.salutation || 'To Whom It May Concern,',
      }
    };

    setPreviewCertificate(previewData);
    setShowPreview(true);
  };

  const resetCertificateForm = () => {
    setCertificateForm({
      employeeId: '',
      exitRequestId: '',
      employmentDetails: {
        joinDate: '',
        lastWorkingDate: '',
        employmentType: 'full-time',
        workLocation: '',
        reportingManager: '',
        reasonForLeaving: 'resignation',
        reasonDescription: ''
      },
      performanceDetails: {
        overallRating: 'good',
        keyAchievements: [{ achievement: '', year: new Date().getFullYear() }],
        skills: [],
        performanceNotes: '',
        performanceRemark: ''
      },
      financialDetails: {
        lastSalary: '',
        currency: 'INR',
        salaryMentioned: false,
        benefits: [{ benefitType: '', amount: '' }],
        finalSettlement: '',
        pendingDues: ''
      },
      authorization: {
        signatoryId: '',
        digitalSignatureUrl: '',
        companySealUrl: ''
      },
      certificateContent: {
        subject: '',
        salutation: 'To Whom It May Concern,',
        body: '',
        closing: 'Yours sincerely,',
        signature: '',
        designation: '',
        companyName: '',
        companyAddress: ''
      },
      documentControl: {
        letterNumber: '',
        issueDate: '',
        isLocked: false,
        status: 'draft'
      },
      additionalRemark: '',
      remarks: ''
    });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedCertificates = useMemo(() => {
    if (!sortConfig.key) return certificates;

    return [...certificates].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [certificates, sortConfig]);

  // Filter certificates
  const filteredCertificates = sortedCertificates;


  // Download certificate as PDF
  const handleDownloadCertificate = async (certificate) => {
    try {
      const blob = await get(`/experience-certificate/${certificate._id}/download`, {
        responseType: 'blob',
      });

      // Create a link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Get filename - fallback to employee name if headers aren't available
      const employeeName = certificate.employeeName || certificate.employeeId?.fullName || (certificate.employeeId?.personalInfo ? `${certificate.employeeId.personalInfo.firstName || ''} ${certificate.employeeId.personalInfo.lastName || ''}`.trim() : '') || 'Employee';
      const fileName = `Experience_Certificate_${employeeName.replace(/\s+/g, '_')}.pdf`;

      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setAlertInfo({
        type: 'success',
        title: 'Download Complete',
        message: 'Experience Certificate PDF downloaded successfully!'
      });
    } catch (error) {
      setAlertInfo({
        type: 'error',
        title: 'Download Failed',
        message: 'Failed to download PDF: ' + error.message
      });
    }
  };

  // Render certificate form
  const renderCertificateForm = () => {
    const joinDateLabel = certificateForm.employmentDetails.joinDate ? formatDate(certificateForm.employmentDetails.joinDate, { includeTime: false }) : '—';
    const lastWorkingDateLabel = certificateForm.employmentDetails.lastWorkingDate ? formatDate(certificateForm.employmentDetails.lastWorkingDate, { includeTime: false }) : '—';
    const selectedEmployeePosition = selectedEmployeeRecord?.employmentInfo?.position || selectedEmployeeRecord?.position || '—';

    // Manager Details
    const managerObj = selectedEmployeeRecord?.employmentInfo?.reportingManager;
    const managerName = managerObj?.fullName ||
      managerObj?.name ||
      (managerObj?.personalInfo ? `${managerObj.personalInfo.firstName || ''} ${managerObj.personalInfo.lastName || ''}`.trim() : '') ||
      (typeof managerObj === 'string' ? managerObj : '—');
    const managerEmail = managerObj?.contactInfo?.workEmail ||
      managerObj?.contactInfo?.personalEmail ||
      managerObj?.email ||
      managerObj?.personalEmail ||
      '—';
    const managerDept = managerObj?.employmentInfo?.department || managerObj?.department || '—';

    return (
      <div className="rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex items-center space-x-2">
            <Award className="h-5 w-5 text-agrosaf-green" />
            <h3 className="text-lg font-bold text-gray-900 tracking-tight">Generate Experience Certificate</h3>
          </div>
          <button
            onClick={() => {
              setShowCertificateForm(false);
              resetCertificateForm();
            }}
            className="p-2 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleGenerateCertificate} className="divide-y divide-gray-100">
          {/* Section 1: Employee Selection & Basic Info */}
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="relative space-y-2" ref={employeeDropdownRef}>
                <label className="block text-sm font-semibold text-gray-700" htmlFor="employee-picker">
                  Employee for Certificate <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <button
                    id="employee-picker"
                    type="button"
                    onClick={() => setIsEmployeeDropdownOpen((o) => !o)}
                    className={`flex w-full items-center justify-between rounded-lg border ${formErrors.employeeId ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'} bg-white px-4 py-2.5 text-left text-sm transition-all focus:ring-2 focus:ring-agrosaf-green`}
                  >
                    <span className="truncate">
                      {selectedEmployeeName && selectedEmployeeName !== '—' ? (
                        <span className="font-medium text-gray-900">{capitalizeWords(selectedEmployeeName)}</span>
                      ) : (
                        <span className="text-gray-400">Search approved exits...</span>
                      )}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-gray-400 transform transition-transform ${isEmployeeDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isEmployeeDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-2xl animate-in fade-in slide-in-from-top-2">
                      <div className="p-2">
                        <input
                          type="text"
                          value={employeeSearchTerm}
                          onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                          placeholder="Search name/ID..."
                          className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:ring-1 focus:ring-agrosaf-green outline-none"
                          autoFocus
                        />
                      </div>
                      <ul className="max-h-60 overflow-y-auto py-1">
                        {isLoadingEmployees ? (
                          <li className="px-4 py-2 text-xs text-gray-400 italic">Searching database...</li>
                        ) : employees.length > 0 ? (
                          employees.map((emp) => (
                            <li
                              key={emp._id}
                              onClick={() => {
                                setCertificateForm(prev => ({ ...prev, employeeId: String(emp._id) }));
                                setIsEmployeeDropdownOpen(false);
                                validateField('employeeId', emp._id);
                              }}
                              className="px-4 py-2.5 text-sm hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-50 last:border-0"
                            >
                              <div className="font-semibold text-gray-800">{emp.fullName}</div>
                              <div className="text-xs text-gray-500">{emp.employeeId} • {emp.department}</div>
                              <div className="text-xs text-gray-400 truncate">{emp.officialEmail || emp.email}</div>
                            </li>
                          ))
                        ) : (
                          <li className="px-4 py-3 text-sm text-gray-500 text-center">No approved exits found</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Selected Employee Card (Added to match Signatory style) */}
                {selectedEmployeeRecord && (
                  <div className="p-3 bg-agrosaf-green/5 border border-agrosaf-green/20 rounded-md mt-2 relative">
                    <button
                      type="button"
                      onClick={() => {
                        setCertificateForm(prev => ({
                          ...prev,
                          employeeId: '',
                          exitRequestId: '',
                          employmentDetails: {
                            ...prev.employmentDetails,
                            joinDate: '',
                            lastWorkingDate: '',
                            reportingManager: '',
                            reasonForLeaving: 'resignation',
                            reasonDescription: ''
                          }
                        }));
                      }}
                      className="absolute top-2 right-2 p-1 hover:bg-white rounded border border-transparent hover:border-agrosaf-green/30 text-gray-400 hover:text-red-500 transition-all"
                      title="Clear selection"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 rounded-full bg-agrosaf-green/10 flex items-center justify-center text-agrosaf-green">
                          <User className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-400 font-bold uppercase tracking-tight leading-none mb-0.5">Selected Employee</div>
                          <div className="text-sm font-bold text-gray-800 leading-tight">
                            {capitalizeWords(selectedEmployeeName)}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-agrosaf-green/10">
                        <div className="flex flex-col">
                          <span className="text-[9px] text-gray-400 font-bold uppercase tracking-tight">Employee ID</span>
                          <span className="text-xs font-mono font-bold text-agrosaf-green">{selectedEmployeeRecord.employeeId || selectedEmployeeRecord.id || 'N/A'}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[9px] text-gray-400 font-bold uppercase tracking-tight">Current Role</span>
                          <span className="text-xs font-bold text-gray-700 uppercase">{selectedEmployeePosition || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="flex flex-col pt-1">
                        <span className="text-[9px] text-gray-400 font-bold uppercase tracking-tight">Department</span>
                        <span className="text-xs text-gray-600">{selectedEmployeeRecord.employmentInfo?.department || selectedEmployeeRecord.department || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}
                {formErrors.employeeId && <p className="text-xs text-red-500 font-medium mt-1">{formErrors.employeeId}</p>}
              </div>

              {/* Authorized Signatory Selection */}
              <div className="space-y-2 relative" ref={signatoryDropdownRef}>
                <label className="block text-sm font-semibold text-gray-700">
                  Authorized Signatory <span className="text-red-500">*</span>
                </label>
                <div
                  onClick={() => setIsSignatoryDropdownOpen(!isSignatoryDropdownOpen)}
                  className={`w-full px-3 py-2.5 border ${isSignatoryDropdownOpen ? 'ring-2 ring-blue-500 border-blue-500' :
                    formErrors['authorization.signatoryId'] ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg bg-white cursor-pointer flex justify-between items-center`}
                >
                  <span className={certificateForm.certificateContent?.signature ? 'text-gray-900 text-sm font-medium' : 'text-gray-400 text-sm'}>
                    {certificateForm.certificateContent?.signature || 'Select HR Manager / Admin...'}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isSignatoryDropdownOpen ? 'rotate-180' : ''}`} />
                </div>
                {formErrors['authorization.signatoryId'] && (
                  <p className="text-xs text-red-500 font-medium">{formErrors['authorization.signatoryId']}</p>
                )}

                {isSignatoryDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-2xl overflow-hidden">
                    {/* Search */}
                    <div className="p-2 border-b bg-gray-50">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search signatory..."
                          value={signatorySearchTerm}
                          onChange={(e) => setSignatorySearchTerm(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>
                    </div>
                    <ul className="max-h-60 overflow-auto py-1">
                      {(() => {
                        const term = signatorySearchTerm.trim().toLowerCase();
                        const filteredSigs = term
                          ? signatories.filter(sig => {
                            const name = (sig.fullName ||
                              (sig.personalInfo ? `${sig.personalInfo.firstName || ''} ${sig.personalInfo.lastName || ''}`.trim() : '') ||
                              sig.name || '').toLowerCase();
                            const id = (sig.employeeId || '').toLowerCase();
                            const email = (sig.contactInfo?.workEmail || sig.email || '').toLowerCase();
                            const dept = (sig.employmentInfo?.department || sig.department || '').toLowerCase();
                            return name.includes(term) || id.includes(term) || email.includes(term) || dept.includes(term);
                          })
                          : signatories;

                        if (isLoadingSignatories) return (
                          <li className="px-3 py-4 text-center text-sm text-gray-500 flex flex-col items-center">
                            <RefreshCw className="h-5 w-5 animate-spin mb-2 text-blue-500" />
                            Finding authorized personnel...
                          </li>
                        );
                        if (filteredSigs.length === 0) return (
                          <li className="px-3 py-4 text-center text-sm text-gray-500">
                            {term ? `No signatories matching "${term}"` : 'No HR Managers or Admins found'}
                          </li>
                        );
                        return filteredSigs.map((sig) => {
                          const sigName = sig.fullName ||
                            (sig.personalInfo ? `${sig.personalInfo.firstName || ''} ${sig.personalInfo.lastName || ''}`.trim() : '') ||
                            sig.name || 'Unknown';
                          return (
                            <li
                              key={sig._id || sig.id}
                              onClick={() => {
                                setCertificateForm(prev => ({
                                  ...prev,
                                  authorization: { ...(prev.authorization || {}), signatoryId: sig._id || sig.id },
                                  certificateContent: {
                                    ...prev.certificateContent,
                                    signature: sigName,
                                    designation: sig.employmentInfo?.position || sig.employmentInfo?.designation || sig.designation || 'Signatory'
                                  }
                                }));
                                setSelectedSignatory(sig);
                                setIsSignatoryDropdownOpen(false);
                                setSignatorySearchTerm('');
                                validateField('authorization.signatoryId', sig._id || sig.id);
                              }}
                              className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0"
                            >
                              <div className="flex justify-between items-start">
                                <div className="font-semibold text-sm text-gray-900">{sigName}</div>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold uppercase">
                                  {sig.role || sig.userRole || 'Staff'}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-y-1">
                                <span className="bg-gray-100 px-1 rounded mr-2 font-mono">{sig.employeeId || sig.id || 'N/A'}</span>
                                <span className="bg-blue-50 text-blue-700 px-1 rounded mr-2 font-medium">
                                  {sig.employmentInfo?.department || sig.department || 'No Dept'}
                                </span>
                              </div>
                            </li>
                          );
                        });
                      })()}
                    </ul>
                  </div>
                )}

                {/* Selected Signatory Card */}
                {selectedSignatory && (
                  <div className="p-3 bg-blue-50/50 border border-blue-100 rounded-md mt-2 relative">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedSignatory(null);
                        setCertificateForm(prev => ({
                          ...prev,
                          authorization: { ...(prev.authorization || {}), signatoryId: '' },
                          certificateContent: { ...prev.certificateContent, signature: '', designation: '' }
                        }));
                      }}
                      className="absolute top-2 right-2 p-1 hover:bg-white rounded border border-transparent hover:border-blue-200 text-gray-400 hover:text-red-500 transition-all"
                      title="Clear selection"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                          <User className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-400 font-bold uppercase tracking-tight leading-none mb-0.5">Authorized Personnel</div>
                          <div className="text-sm font-bold text-gray-800 leading-tight">
                            {selectedSignatory.fullName ||
                              (selectedSignatory.personalInfo ? `${selectedSignatory.personalInfo.firstName || ''} ${selectedSignatory.personalInfo.lastName || ''}`.trim() : '') ||
                              selectedSignatory.name || 'Unknown'}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-blue-100">
                        <div className="flex flex-col">
                          <span className="text-[9px] text-gray-400 font-bold uppercase tracking-tight">Official ID</span>
                          <span className="text-xs font-mono font-bold text-blue-600">{selectedSignatory.employeeId || selectedSignatory.id || 'N/A'}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[9px] text-gray-400 font-bold uppercase tracking-tight">Role</span>
                          <span className="text-xs font-bold text-blue-700 uppercase">{selectedSignatory.role || selectedSignatory.userRole || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="flex flex-col pt-1">
                        <span className="text-[9px] text-gray-400 font-bold uppercase tracking-tight">Department</span>
                        <span className="text-xs text-gray-600">{selectedSignatory.employmentInfo?.department || selectedSignatory.department || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {selectedEmployeeRecord && (
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 animate-in zoom-in-95">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-8 text-[11px]">
                  <div>
                    <span className="text-blue-500 font-bold uppercase block mb-1">Date of Joining</span>
                    <span className="text-gray-900 font-semibold text-sm">{joinDateLabel}</span>
                  </div>
                  <div>
                    <span className="text-blue-500 font-bold uppercase block mb-1">Last Working Date</span>
                    <span className="text-gray-900 font-semibold text-sm">{lastWorkingDateLabel}</span>
                  </div>
                  <div>
                    <span className="text-blue-500 font-bold uppercase block mb-1">Tenure (Worked)</span>
                    <span className="text-gray-900 font-semibold text-sm">{tenureLabel}</span>
                  </div>

                  <div className="bg-white/60 rounded-lg p-3 border border-blue-100 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-blue-100/30 rounded-full -mr-8 -mt-8"></div>
                    <span className="text-blue-600 font-bold uppercase block mb-2 text-[10px] tracking-wider">Reporting Manager</span>
                    <div className="space-y-1 relative z-10">
                      <div className="text-gray-900 font-bold text-sm leading-tight break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{managerName}</div>
                      <div className="text-gray-500 truncate lowercase">{managerEmail}</div>
                      <div className="text-gray-600 font-medium italic truncate" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{managerDept}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 2: Performance & Remarks */}
          <div className="p-6 bg-gray-50/50 space-y-6">
            <h4 className="text-sm font-bold text-gray-900 flex items-center">
              Performance & Ratings
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Overall Performance Rating</label>
                <select
                  value={certificateForm.performanceDetails?.overallRating || ''}
                  onChange={(e) => setCertificateForm(prev => ({ ...prev, performanceDetails: { ...(prev.performanceDetails || {}), overallRating: e.target.value } }))}
                  className="w-full rounded-lg border-gray-300 px-4 py-2 text-sm focus:ring-agrosaf-green focus:border-agrosaf-green"
                >
                  <option value="excellent">Excellent</option>
                  <option value="very-good">Very Good</option>
                  <option value="good">Good</option>
                  <option value="satisfactory">Satisfactory</option>
                  <option value="needs-improvement">Needs Improvement</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Performance Remark <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={certificateForm.performanceDetails?.performanceRemark || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.length <= 200) {
                      setCertificateForm(prev => ({ ...prev, performanceDetails: { ...(prev.performanceDetails || {}), performanceRemark: val } }));
                      validateField('performance.performanceRemark', val);
                    }
                  }}
                  onKeyPress={handleTextKeyPress}
                  maxLength={200}
                  placeholder="e.g. He/She was an exceptional contributor..."
                  className={`w-full rounded-lg border ${formErrors['performance.performanceRemark'] ? 'border-red-500' : 'border-gray-300'} px-4 py-2 text-sm focus:ring-agrosaf-green focus:border-agrosaf-green`}
                />
                <div className="flex justify-between items-start mt-1">
                  <div className="flex-1">
                    {formErrors['performance.performanceRemark'] && <p className="text-xs text-red-500 font-medium">{formErrors['performance.performanceRemark']}</p>}
                  </div>
                  <span className={`text-[10px] font-bold ${(certificateForm.performanceDetails?.performanceRemark || '').length >= 190 ? 'text-red-500' : 'text-gray-400'}`}>
                    {(certificateForm.performanceDetails?.performanceRemark || '').length}/200
                  </span>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Additional Remarks (Internal Use)</label>
              <textarea
                rows={3}
                value={certificateForm.remarks}
                onChange={(e) => {
                  const val = e.target.value.slice(0, 500);
                  setCertificateForm(prev => ({ ...prev, remarks: val }));
                  validateField('remarks', val);
                }}
                placeholder="Internal auditors notes or processing remarks..."
                className={`w-full rounded-lg border ${formErrors.remarks ? 'border-red-500' : 'border-gray-300'} px-4 py-2 text-sm focus:ring-agrosaf-green focus:border-agrosaf-green`}
              />
              <div className="flex justify-between items-center text-[10px] text-gray-400">
                <span>{formErrors.remarks || ''}</span>
                <span>{certificateForm.remarks.length}/500 characters</span>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="bg-gray-50 px-6 py-4 flex flex-col md:flex-row space-y-3 md:space-y-0 md:justify-between items-center">
            <div className="flex items-center space-x-2">
              <span className="text-xs font-medium text-gray-500">Draft Status:</span>
              <span className="text-xs font-bold text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full">DRAFT-MODE</span>
            </div>
            <div className="flex space-x-3 w-full md:w-auto">
              <button
                type="button"
                onClick={() => {
                  setShowCertificateForm(false);
                  resetCertificateForm();
                }}
                className="flex-1 md:flex-none px-6 py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-900 bg-transparent transition-colors"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handlePreviewFromForm}
                className="flex-1 md:flex-none flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all"
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </button>

            </div>
          </div>
        </form>
      </div>
    );
  };


  // Render all certificates table
  const renderAllCertificates = () => (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Award className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Certificates</p>
              <p className="text-2xl font-semibold text-gray-900">{formatIndianNumber(certificateStats.totalCertificates || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Draft</p>
              <p className="text-2xl font-semibold text-gray-900">{formatIndianNumber(certificateStats.draftCertificates || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Sent</p>
              <p className="text-2xl font-semibold text-gray-900">{formatIndianNumber(certificateStats.sentCertificates || 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search Bar */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or certificate ID..."
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={handleSearchKeyPress}
              maxLength={100}
              autoFocus
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  // Maintain focus on input
                  const input = document.activeElement;
                  if (input && input.tagName === 'INPUT') {
                    setTimeout(() => input.focus(), 0);
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="relative flex-shrink-0" ref={statusDropdownRef}>
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className={`flex items-center space-x-2 px-3 py-2 border rounded-lg hover:bg-gray-50 transition-all ${statusFilter !== 'all' ? 'border-agrosaf-green bg-green-50' : 'border-gray-300 bg-white'
                }`}
            >
              <Filter className={`h-4 w-4 ${statusFilter !== 'all' ? 'text-agrosaf-green' : 'text-gray-400'}`} />
              <span className="text-sm text-gray-700">
                {statusFilter === 'all' ? 'All Status' : capitalizeWords(statusFilter)}
              </span>
              <ChevronDown className={`h-4 w-4 text-gray-400 transform transition-transform ${isStatusDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isStatusDropdownOpen && (
              <div className="absolute z-50 mt-1 min-w-[150px] bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2">
                <div className="py-1">
                  {['all', 'draft', 'sent'].map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setStatusFilter(status);
                        setIsStatusDropdownOpen(false);
                        setCertificatesPage(1);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${statusFilter === status ? 'bg-green-50 text-agrosaf-green font-semibold' : 'text-gray-700'
                        }`}
                    >
                      {status === 'all' ? 'All Status' : capitalizeWords(status)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Department Filter */}
          <div className="relative flex-shrink-0" ref={departmentDropdownRef}>
            <button
              onClick={() => setIsDepartmentDropdownOpen(!isDepartmentDropdownOpen)}
              className={`flex items-center space-x-2 px-3 py-2 border rounded-lg hover:bg-gray-50 transition-all ${departmentFilter !== 'all' ? 'border-agrosaf-green bg-green-50' : 'border-gray-300 bg-white'
                }`}
            >
              <Building className={`h-4 w-4 ${departmentFilter !== 'all' ? 'text-agrosaf-green' : 'text-gray-400'}`} />
              <span className="text-sm text-gray-700 max-w-[150px] truncate">
                {departmentFilter === 'all' ? 'All Departments' : departmentFilter}
              </span>
              <ChevronDown className={`h-4 w-4 text-gray-400 transform transition-transform ${isDepartmentDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isDepartmentDropdownOpen && (
              <div className="absolute z-50 mt-1 min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2">
                <div className="max-h-60 overflow-y-auto py-1">
                  <button
                    onClick={() => {
                      setDepartmentFilter('all');
                      setIsDepartmentDropdownOpen(false);
                      setCertificatesPage(1);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${departmentFilter === 'all' ? 'bg-green-50 text-agrosaf-green font-semibold' : 'text-gray-700'
                      }`}
                  >
                    All Departments
                  </button>
                  {departments.map((dept) => (
                    <button
                      key={dept}
                      onClick={() => {
                        setDepartmentFilter(dept);
                        setIsDepartmentDropdownOpen(false);
                        setCertificatesPage(1);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${departmentFilter === dept ? 'bg-green-50 text-agrosaf-green font-semibold' : 'text-gray-700'
                        }`}
                    >
                      {dept}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Date Range Filter — filters by Created Date */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onChange={({ startDate, endDate }) => {
                setStartDate(startDate);
                setEndDate(endDate);
                setCertificatesPage(1);
              }}
              containerClassName="flex items-center space-x-2"
            />
          </div>

          {/* Clear Filters Button */}
          {(searchTerm || statusFilter !== 'all' || departmentFilter !== 'all' || startDate || endDate) && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setDepartmentFilter('all');
                  setStartDate('');
                  setEndDate('');
                  setCertificatesPage(1);
                  setSortConfig({ key: 'createdAt', direction: 'desc' });
                }}
                className="flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Clear</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Certificates Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Experience Certificates</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={certificates.length > 0 && selectedRows.length === certificates.length}
                    className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('certificateId')}>
                  <div className="flex items-center space-x-1">
                    <span>Certificate ID</span>
                    {sortConfig.key === 'certificateId' ? (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('employeeName')}>
                  <div className="flex items-center space-x-1">
                    <span>Employee</span>
                    {sortConfig.key === 'employeeName' ? (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('department')}>
                  <div className="flex items-center space-x-1">
                    <span>Department</span>
                    {sortConfig.key === 'department' ? (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {sortConfig.key === 'status' ? (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('createdAt')}>
                  <div className="flex items-center space-x-1">
                    <span>Created</span>
                    {sortConfig.key === 'createdAt' ? (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCertificates.map((certificate) => (
                <tr key={certificate._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(certificate._id)}
                      onChange={() => handleSelectRow(certificate._id)}
                      className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{certificate.certificateId}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap max-w-xs">
                    <div className="flex items-center min-w-0">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {capitalizeWords(certificate.employeeName || certificate.employeeId?.fullName || (certificate.employeeId?.personalInfo ? `${certificate.employeeId.personalInfo.firstName || ''} ${certificate.employeeId.personalInfo.lastName || ''}`.trim() : '') || '—')}
                        </div>
                        <div className="text-sm text-gray-500 truncate" title={certificate.officialEmail || certificate.employeeEmail}>
                          {certificate.officialEmail || certificate.employeeEmail || '—'}
                        </div>
                        {certificate.officialEmail && certificate.employeeEmail && certificate.officialEmail !== certificate.employeeEmail && (
                          <div className="text-xs text-gray-400 truncate" title={`Personal: ${certificate.employeeEmail}`}>
                            {certificate.employeeEmail}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap max-w-xs">
                    <div className="text-sm text-gray-900 truncate">{certificate.department}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-bold rounded-full uppercase tracking-wider ${certificate.status === 'draft' || certificate.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      certificate.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                        certificate.status === 'issued' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                      }`}>
                      {certificate.status === 'issued' ? (
                        <span className="flex items-center"><CheckCircle className="h-3 w-3 mr-1" /> Issued</span>
                      ) : (certificate.status === 'draft' || certificate.status === 'pending' ? 'Draft' : capitalizeWords(certificate.status))}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(certificate.createdAt, { includeTime: true })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-3">
                      <PermissionGate permission="exit_management" action="read">
                        <button
                          onClick={() => handleViewCertificate(certificate)}
                          className="text-gray-600 hover:text-gray-900 transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadCertificate(certificate)}
                          className="text-agrosaf-green hover:text-agrosaf-darkGreen transition-colors"
                          title="Download PDF"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </PermissionGate>

                      {certificate.status !== 'issued' && (
                        <PermissionGate permission="exit_management" action="update">
                          <button
                            onClick={() => handleSendCertificateClick(certificate)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="Send Email"
                          >
                            <Send className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      )}

                      {certificate.status !== 'issued' && (
                        <PermissionGate permission="exit_management" action="delete">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteCertificate(certificate._id);
                            }}
                            className="text-red-500 hover:text-red-700 transition-colors"
                            title="Delete Draft"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      )}

                      {certificate.status === 'issued' && (
                        <Clock className="h-4 w-4 text-gray-400" title="Record Locked" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredCertificates.length === 0 && (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No experience certificates found</p>
            </div>
          )}
        </div>
      </div>
      {/* Pagination footer */}
      {certificatesTotalItems > certificatesPageSize && (
        <div className="flex items-center justify-between py-3">
          <div className="text-sm text-gray-600">
            Showing {certificates.length > 0 ? (certificatesPage - 1) * certificatesPageSize + 1 : 0}
            {' '}–{' '}
            {Math.min(certificatesPage * certificatesPageSize, certificatesTotalItems)} of {certificatesTotalItems}
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Rows per page</span>
              <select
                value={certificatesPageSize}
                onChange={(e) => { setCertificatesPageSize(parseInt(e.target.value, 10)); setCertificatesPage(1); }}
                className="px-2 py-1 border border-gray-300 rounded-md"
              >
                {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCertificatesPage(p => Math.max(1, p - 1))}
                disabled={certificatesPage <= 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >Prev</button>
              <span className="text-sm text-gray-700">Page {certificatesPage} of {certificatesTotalPages}</span>
              <button
                onClick={() => setCertificatesPage(p => Math.min(certificatesTotalPages, p + 1))}
                disabled={certificatesPage >= certificatesTotalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >Next</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Bulk delete certificates handler
  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} experience certificate(s)? This action cannot be undone.`)) return;

    setIsBulkDeleting(true);
    const idsToDelete = Array.from(selectedRows);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Process deletions in parallel batches of 5 to speed up but not overwhelm the server
      const batchSize = 5;
      for (let i = 0; i < idsToDelete.length; i += batchSize) {
        const batch = idsToDelete.slice(i, i + batchSize);
        await Promise.all(batch.map(async (id) => {
          try {
            await del(`/experience-certificate/${id}`);
            successCount++;
          } catch (err) {
            errorCount++;
          }
        }));
      }

      if (successCount > 0) {
        setAlertInfo({
          type: 'success',
          title: 'Success',
          message: `Successfully deleted ${successCount} certificate(s)`
        });
        setSelectedRows([]);
        queryClient.invalidateQueries(['experienceCertificates']);
        queryClient.invalidateQueries(['experienceCertificateStatistics']);
      }

      if (errorCount > 0) {
        setAlertInfo({
          type: 'error',
          title: 'Partial Failure',
          message: `Failed to delete ${errorCount} certificate(s). Please try again.`
        });
      }
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Preview certificate PDF in new tab
  const handlePreviewCertificate = async (certificate) => {
    try {
      const blob = await get(`/experience-certificate/${certificate._id}/download`, {
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');

      // Note: We don't revokeObjectURL here immediately because it would break the new tab.
      // Browsers handle it, but for a true clean up one might want to manage this differently.

    } catch (error) {
      setAlertInfo({
        type: 'error',
        title: 'Preview Failed',
        message: 'Failed to preview PDF: ' + error.message
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Experience Certificate Management</h1>
        </div>
        <div className="flex items-center space-x-4">
          <PermissionGate permission="exit_management" action="delete">
            {selectedRows.length > 0 && (
              <Button
                variant="danger"
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
                className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white"
              >
                {isBulkDeleting ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span>Delete Selected ({selectedRows.length})</span>
              </Button>
            )}
          </PermissionGate>

          <PermissionGate permission="exit_management" action="read">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                onClick={handleExportClick}
                disabled={exportLoading}
                className={
                  selectedRows.length > 0
                    ? 'bg-blue-50 border-blue-200 text-blue-600'
                    : ''
                }
              >
                <Download className="h-4 w-4 mr-2" />
                {selectedRows.length > 0
                  ? `Export Selected (${selectedRows.length})`
                  : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.length > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="exit_management" action="create">
            <button
              onClick={() => {
                setShowCertificateForm(true);
              }}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-agrosaf-green border border-transparent rounded-md hover:bg-agrosaf-darkGreen focus:outline-none focus:ring-2 focus:ring-agrosaf-green"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Certificate
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Content */}
      {isLoadingCertificates ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : showCertificateForm ? (
        renderCertificateForm()
      ) : (
        renderAllCertificates()
      )}


      {/* Preview Modal - Certificate Format */}
      {showPreview && previewCertificate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 !mt-0 flex items-center justify-center p-4">
          <div className="relative bg-white shadow-2xl rounded-lg w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center shrink-0">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Experience Certificate Preview</h3>
                <p className="text-sm text-gray-500">Review and edit the content below before saving or downloading.</p>
              </div>
              <button
                onClick={() => {
                  setShowPreview(false);
                  setPreviewCertificate(null);
                }}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-200 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body - Scrollable Certificate Area */}
            <div className="flex-1 overflow-y-auto bg-gray-100 p-4 md:p-8">
              <div className="bg-white mx-auto shadow-xl w-full min-h-[297mm] max-w-[210mm] p-[10mm] text-[#334155] font-sans leading-relaxed relative">

                {/* Border */}
                <div className="absolute inset-[10px] border border-gray-300 pointer-events-none" />

                {/* Certificate Header: Logo Left, Company Name Center */}
                <div className="relative mb-12 flex items-center">
                  <div className="absolute left-0 top-0 w-20">
                    <img src="/logo.png" className="max-w-full" alt="Logo"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div className="flex-1 text-center">
                    <h1 className="text-2xl font-bold text-[#3B82F6]">{process.env.REACT_APP_COMPANY_NAME}</h1>
                    <h2 className="text-xl font-bold text-[#3B82F6] mt-4">EXPERIENCE CERTIFICATE</h2>
                  </div>
                </div>

                {/* Date & Certificate No - Right Aligned & Read-Only */}
                <div className="text-right mb-10 text-[15px]">
                  <div className="inline-flex flex-col items-end space-y-1">
                    <div className="flex items-center space-x-1">
                      <span className="font-medium shrink-0">Certificate No:</span>
                      <input
                        className="ml-1 border-none p-0 focus:ring-0 focus:bg-blue-50 rounded bg-transparent text-right w-full"
                        value={certificateForm.documentControl?.letterNumber || previewCertificate?.certificateId || ''}
                        placeholder="Auto-generated on Save"
                        onChange={(e) => setCertificateForm(prev => ({
                          ...prev,
                          documentControl: { ...prev.documentControl, letterNumber: e.target.value }
                        }))}
                        style={{ width: '180px' }}
                      />
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="font-medium shrink-0">Date:</span>
                      <input
                        className="ml-1 border-none p-0 focus:ring-0 focus:bg-blue-50 rounded bg-transparent text-right"
                        value={certificateForm.documentControl?.issueDate || ''}
                        onChange={(e) => setCertificateForm(prev => ({
                          ...prev,
                          documentControl: { ...prev.documentControl, issueDate: e.target.value }
                        }))}
                        style={{ width: '180px' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Salutation - Editable */}
                <div className="mb-6 text-[15px]">
                  <input
                    className="w-full border-none p-0 focus:ring-0 focus:bg-blue-50 rounded bg-transparent font-medium"
                    value={certificateForm.certificateContent?.salutation || 'To Whom It May Concern,'}
                    onChange={(e) => setCertificateForm(prev => ({ ...prev, certificateContent: { ...prev.certificateContent, salutation: e.target.value } }))}
                  />
                </div>

                {/* Body - Editable Textarea */}
                <div className="mb-12 text-[15px]">
                  <textarea
                    className="w-full border-none p-0 focus:ring-0 focus:bg-blue-50 rounded bg-transparent resize-none leading-[1.8] text-justify"
                    rows={14}
                    value={certificateForm.certificateContent?.body || generateDefaultCertificateBody(certificateForm, employees)}
                    onChange={(e) => setCertificateForm(prev => ({ ...prev, certificateContent: { ...prev.certificateContent, body: e.target.value } }))}
                  />
                </div>

                {/* Signature Block - Left Aligned */}
                <div className="text-[15px] space-y-1">
                  <p className="mb-4">{process.env.REACT_APP_COMPANY_NAME},</p>
                  <p className="mb-8">(Authorized Signatory)</p>

                  <div className="pt-4 space-y-2">
                    <div className="flex items-center">
                      <span className="w-20">Name:</span>
                      <input
                        className="flex-1 font-bold border-none p-0 focus:ring-0 focus:bg-blue-50 rounded bg-transparent uppercase"
                        value={certificateForm.certificateContent?.signature || ''}
                        onChange={(e) => setCertificateForm(prev => ({ ...prev, certificateContent: { ...prev.certificateContent, signature: e.target.value } }))}
                      />
                    </div>
                    <div className="flex items-center">
                      <span className="w-24">Designation:</span>
                      <select
                        className="flex-1 border-none p-0 capitalize focus:ring-0 focus:bg-blue-50 rounded bg-transparent"
                        value={certificateForm.certificateContent?.designation || ''}
                        onChange={(e) => setCertificateForm(prev => ({ ...prev, certificateContent: { ...prev.certificateContent, designation: e.target.value } }))}
                        disabled={loadingDesignationMasters}
                      >
                        <option value="">{loadingDesignationMasters ? 'Loading designations...' : 'Select designation'}</option>
                        {designationMasters.map((designation) => {
                          const id = String(designation.id || designation._id);
                          const title = designation.title || designation.name || '';
                          return <option key={id} value={title}>{title}</option>;
                        })}
                      </select>
                    </div>
                    <div className="flex items-center">
                      <span className="w-24 shrink-0">Seal/Stamp:</span>
                      <div className="border-b border-gray-400 w-48 h-5 ml-1" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer - Actions */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3 shrink-0">
              <button
                onClick={() => {
                  setShowPreview(false);
                  setPreviewCertificate(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Back to Form
              </button>

              {/* <button
                onClick={async () => {
                  const certWithEdits = {
                    ...previewCertificate,
                    certificateContent: certificateForm.certificateContent,
                    documentControl: certificateForm.documentControl
                  };
                  await handleDownloadCertificate(certWithEdits);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 transition-colors flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </button> */}

              <button
                onClick={() => {
                  setShowPreview(false);
                  setPreviewCertificate(null);
                  // Fire submit with validated data
                  if (validateAll()) {
                    handleGenerateCertificate({ preventDefault: () => { } });
                  } else {
                    setAlertInfo({ type: 'error', title: 'Data Missing', message: 'Please fix all mandatory fields before saving.' });
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 transition-colors flex items-center"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Finalize &amp; Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Experience Certificates"
        showDateRange
        scopedFormatExport={{
          onExport: handleScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showAll: true,
          showDateRange: true,
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4 !mt-0">
          <div className="relative mx-auto border w-full max-w-md shadow-2xl rounded-lg bg-white">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Action</h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete this certificate?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  No
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Confirmation Modal */}
      {showSendModal && certificateToSend && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Send Experience Certificate</h3>
                <button
                  onClick={handleCloseSendModal}
                  disabled={isSending}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Are you sure you want to send the experience certificate to <span className="font-semibold break-all">{certificateToSend.officialEmail || certificateToSend.employeeEmail || certificateToSend.employeeName || 'email address'}</span>?
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCloseSendModal}
                disabled={isSending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSendCertificate}
                disabled={isSending}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Certificate Details Modal */}
      {showViewModal && certificateToView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 overflow-y-auto p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 my-8">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Experience Certificate Details</h3>
                <button
                  onClick={handleCloseViewModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-6 overflow-y-auto max-h-[calc(100vh-200px)]">
              <div className="space-y-6">
                {/* Certificate ID & Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Certificate ID</p>
                    <p className="text-sm font-semibold text-gray-900">{certificateToView.certificateId || 'N/A'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</p>
                    <span className={`inline-block px-2 py-1 text-xs font-bold rounded-full uppercase tracking-wider ${certificateToView.status === 'draft' || certificateToView.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      certificateToView.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                        certificateToView.status === 'issued' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                      }`}>
                      {certificateToView.status === 'issued' ? 'Issued' :
                        (certificateToView.status === 'draft' || certificateToView.status === 'pending' ? 'Draft' : capitalizeWords(certificateToView.status))}
                    </span>
                  </div>
                </div>

                {/* Employee Information */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">Employee Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Employee Name</p>
                      <p className="text-sm text-gray-900 break-words">
                        {capitalizeWords(certificateToView.employeeName || certificateToView.employeeId?.fullName || (certificateToView.employeeId?.personalInfo ? `${certificateToView.employeeId.personalInfo.firstName || ''} ${certificateToView.employeeId.personalInfo.lastName || ''}`.trim() : '')) || 'N/A'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Employee ID</p>
                      <p className="text-sm text-gray-900">{certificateToView.employeeIdNumber || 'N/A'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Official Email</p>
                      <p className="text-sm text-gray-900 break-all">{certificateToView.officialEmail || 'N/A'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Personal Email</p>
                      <p className="text-sm text-gray-900 break-all">{certificateToView.employeeEmail || 'N/A'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Department</p>
                      <p className="text-sm text-gray-900 break-words">{certificateToView.department || 'N/A'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Position</p>
                      <p className="text-sm text-gray-900 break-words">{certificateToView.position || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Employment Details */}
                {certificateToView.employmentDetails && (
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Employment Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Join Date</p>
                        <p className="text-sm text-gray-900">
                          {certificateToView.employmentDetails.joinDate
                            ? formatDate(certificateToView.employmentDetails.joinDate, { includeTime: false })
                            : 'N/A'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last Working Date</p>
                        <p className="text-sm text-gray-900">
                          {certificateToView.employmentDetails.lastWorkingDate
                            ? formatDate(certificateToView.employmentDetails.lastWorkingDate, { includeTime: false })
                            : 'N/A'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Employment Type</p>
                        <p className="text-sm text-gray-900">{capitalizeWords(certificateToView.employmentDetails.employmentType) || 'N/A'}</p>
                      </div>
                      {certificateToView.employmentDetails.reasonForLeaving && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Reason for Leaving</p>
                          <p className="text-sm text-gray-900 break-words">{capitalizeWords(certificateToView.employmentDetails.reasonForLeaving)}</p>
                        </div>
                      )}
                      {certificateToView.employmentDetails.reasonDescription && (
                        <div className="space-y-1 md:col-span-2">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Reason Description</p>
                          <p className="text-sm text-gray-900 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{certificateToView.employmentDetails.reasonDescription}</p>
                        </div>
                      )}
                      {certificateToView.employmentDetails.totalExperience && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Experience</p>
                          <p className="text-sm text-gray-900">{certificateToView.employmentDetails.totalExperience}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Remarks */}
                {certificateToView.remarks && (
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Remarks</h4>
                    <p className="text-sm text-gray-900 break-words whitespace-pre-wrap" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{certificateToView.remarks}</p>
                  </div>
                )}

                {/* Timestamps */}
                {certificateToView.createdAt && (
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Timestamps</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Created At</p>
                        <p className="text-sm text-gray-900">{formatDate(certificateToView.createdAt, { includeTime: true })}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => handlePreviewCertificate(certificateToView)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
              >
                <FileText className="h-4 w-4 mr-2" />
                Preview Certificate
              </button>
              <button
                onClick={handleCloseViewModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {alertInfo && (
        <div className="fixed top-4 right-4 z-50 max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden">
          <div className="p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {alertInfo.type === 'success' ? (
                  <CheckCircle className="h-6 w-6 text-green-400" aria-hidden="true" />
                ) : (
                  <X className="h-6 w-6 text-red-400" aria-hidden="true" />
                )}
              </div>
              <div className="ml-3 w-0 flex-1 pt-0.5">
                <p className="text-sm font-medium text-gray-900">{alertInfo.title}</p>
                <p className="mt-1 text-sm text-gray-500">{alertInfo.message}</p>
              </div>
              <div className="ml-4 flex-shrink-0 flex">
                <button
                  className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => setAlertInfo(null)}
                >
                  <span className="sr-only">Close</span>
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperienceCertificate;
